// BillingSystem.jsx – FINAL (PART 1/3)

import React, { useState, useEffect } from "react";
import {
  database,
  ref,
  push,
  onValue,
  remove,
  set,
  update,
  runTransaction,
} from "./firebase";
import { get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "./BillingSystem.css";
import BossNavigation from "./BossNavigation";
import Navigation from "./Navigation";

// ✔ Your final product list
const products = [
  "Premium 2 Litre Bottle",
  "Premium 1 Litre Bottle",
  "Premium 500ML Bottle",
  "Premium 250ML Bottle",
  "Green 1 Litre Bottle",
  "Green 500ML Bottle",
  "250ML Pouch",
  "Packed Ice Cube (500 gm)",
];

export default function BillingSystem({ role, phone }) {
  const navigate = useNavigate();

  // ------------------------
  // 🟦 STATE MANAGEMENT
  // ------------------------
  const [quantities, setQuantities] = useState(Array(products.length).fill(0));
  const [schemes, setSchemes] = useState(Array(products.length).fill(0));

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [totalAmount, setTotalAmount] = useState("");
  const [deliveredBy, setDeliveredBy] = useState("");

  const [tokenNumber, setTokenNumber] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [userName, setUserName] = useState("");

  // STOCK SYSTEM
  const [openingStock, setOpeningStock] = useState(Array(products.length).fill(0));
  const [closingStock, setClosingStock] = useState(Array(products.length).fill(0));

  const [soldCartons, setSoldCartons] = useState(Array(products.length).fill(0));
  const [soldBaseline, setSoldBaseline] = useState(Array(products.length).fill(0));

  const [latestCorrectedStock, setLatestCorrectedStock] = useState(
    Array(products.length).fill(0)
  );

  const [displayStock, setDisplayStock] = useState(
    Array(products.length).fill(0)
  );

  // SCHEME (for CSV only, not for stock)
  const [pendingScheme, setPendingScheme] = useState(
    Array(products.length).fill(0)
  );

  // PROFILE & NOTIFICATIONS
  const [showProfile, setShowProfile] = useState(false);
  const [editName, setEditName] = useState("");
  const [notification, setNotification] = useState("");

  const notify = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 3000);
  };

  // ------------------------
  // 🟦 DATE HELPERS
  // ------------------------
  const getTodayKey = () => new Date().toISOString().split("T")[0];

  const formatDate = (d) =>
    `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(
      d.getDate()
    ).padStart(2, "0")}`;

  // ------------------------
  // 🟦 MAIN DATA LOADING
  // ------------------------
  useEffect(() => {
    // USER NAME
    onValue(ref(database, `users/${phone}`), (snap) => {
      const data = snap.val();
      const nm = data?.name || phone;
      setUserName(nm);
      setEditName(nm);
    });

    // TOKEN NUMBER
    onValue(ref(database, `token/${getTodayKey()}`), (snap) => {
      setTokenNumber(snap.val() || 0);
    });

    // OPENING STOCK
    onValue(ref(database, "openingStock"), (snap) => {
      const data = snap.val() || {};
      const arr = products.map((p) => Number(data[p] || 0));
      setOpeningStock(arr);
    });

    // CLOSING STOCK
    onValue(ref(database, "closingStock"), (snap) => {
      const data = snap.val() || {};
      const arr = products.map((p) => Number(data[p] || 0));
      setClosingStock(arr);
    });

    // SOLD BASELINE
    onValue(ref(database, "soldBaseline"), (snap) => {
      const data = snap.val() || {};
      const arr = products.map((p) => Number(data[p] || 0));
      setSoldBaseline(arr);
    });

    // LATEST CORRECTED STOCK (the NEW base)
    onValue(ref(database, "latestCorrectedStock"), (snap) => {
      const data = snap.val() || {};
      const arr = products.map((p) => Number(data[p] || 0));
      setLatestCorrectedStock(arr);
    });

    // INVOICES → SOLD CARTONS & SCHEME
    onValue(ref(database, "invoices"), (snap) => {
      const raw = snap.val() || {};
      const list = Object.entries(raw).map(([id, val]) => ({
        id,
        ...val,
      }));
      list.reverse();
      setInvoices(list);

      const sold = Array(products.length).fill(0);
      const schemeArr = Array(products.length).fill(0);

      list.forEach((inv) => {
        (inv.items || []).forEach(({ product, quantity, scheme }) => {
          const idx = products.indexOf(product);
          if (idx >= 0) {
            sold[idx] += Number(quantity) || 0;
            schemeArr[idx] += Number(scheme) || 0;
          }
        });
      });

      setSoldCartons(sold);
      setPendingScheme(schemeArr);
    });
  }, [phone]);

  // ------------------------
  // 🟦 LIVE STOCK CALCULATION
  // ------------------------
  useEffect(() => {
    const live = products.map((p, i) => {
      const base = Number(latestCorrectedStock[i]) || 0;
      const sold = Number(soldCartons[i]) || 0;
      const baseline = Number(soldBaseline[i]) || 0;
      return base - (sold - baseline);
    });

    setDisplayStock(live);
  }, [latestCorrectedStock, soldCartons, soldBaseline]);

  // ------------------------
  // 🟦 NEW: UNIFIED STOCK CORRECTION BUTTON
  // ------------------------
  const handleCheckLatestStock = async () => {
    // Load opening history
    const openSnap = await get(ref(database, "openingStockHistory"));
    const closeSnap = await get(ref(database, "closingStockHistory"));

    const openData = openSnap.val() || {};
    const closeData = closeSnap.val() || {};

    let latestOpening = null;
    let latestClosing = null;

    // find latest opening
    Object.values(openData).forEach((x) => {
      if (!latestOpening || x.timestamp > latestOpening.timestamp) {
        latestOpening = x;
      }
    });

    // find latest closing
    Object.values(closeData).forEach((x) => {
      if (!latestClosing || x.timestamp > latestClosing.timestamp) {
        latestClosing = x;
      }
    });

    // decide which is newer
    let latest = null;

    if (latestOpening && latestClosing) {
      latest =
        latestOpening.timestamp > latestClosing.timestamp
          ? latestOpening
          : latestClosing;
    } else {
      latest = latestOpening || latestClosing;
    }

    if (!latest) {
      notify("❌ No Opening or Closing Stock found!");
      return;
    }

    // Convert items to base stock
    const baseStock = {};
    products.forEach((p) => (baseStock[p] = 0));

    latest.items.forEach(({ product, cartons }) => {
      baseStock[product] = Number(cartons) || 0;
    });

    // SAVE LATEST BASE
    await set(ref(database, "latestCorrectedStock"), baseStock);

    // RESET BASELINE SOLD
    const soldMap = {};
    products.forEach((p, i) => {
      soldMap[p] = soldCartons[i] || 0;
    });

    await set(ref(database, "soldBaseline"), soldMap);

    notify("✅ Latest Stock Applied & Corrected Successfully!");
  };
// BillingSystem.jsx – PART 2/3 (CONTINUED)

  // ------------------------
  // 🟦 RESET TOKEN
  // ------------------------
  const resetTokenNumber = async () => {
    const today = getTodayKey();
    await set(ref(database, `token/${today}`), 0);
    setTokenNumber(0);
    notify("Token reset to 0");
  };

  // ------------------------
  // 🟦 CREATE / UPDATE INVOICE
  // ------------------------
  const handleGenerateInvoice = async () => {
    if (!customerPhone.match(/^\d{10}$/))
      return notify("Invalid phone number");

    if (!quantities.some((q) => q > 0) && !schemes.some((s) => s > 0))
      return notify("Enter at least one quantity");

    const items = products
      .map((p, i) => ({
        product: p,
        quantity: quantities[i],
        scheme: schemes[i],
      }))
      .filter((x) => x.quantity > 0 || x.scheme > 0);

    if (editingInvoiceId) {
      // EDIT MODE
      const oldSnap = await get(ref(database, `invoices/${editingInvoiceId}`));
      const old = oldSnap.val();
      if (!old) return notify("Original invoice not found!");

      const updated = {
        customerName,
        customerPhone,
        deliveredBy: deliveredBy || "",
        createdBy: userName,
        paymentMethod,
        totalAmount: totalAmount || "",
        date: old.date,
        tokenNumber: old.tokenNumber,
        delivered: old.delivered || false,
        items,
      };

      await update(ref(database, `invoices/${editingInvoiceId}`), updated);
      notify("Invoice updated successfully!");
    } else {
      // NEW INVOICE
      const today = getTodayKey();
      const tRef = ref(database, `token/${today}`);
      let newToken = 1;

      await runTransaction(tRef, (cur) => (cur || 0) + 1).then((res) => {
        if (res.committed) newToken = res.snapshot.val();
      });

      const now = new Date();
      const invoice = {
        customerName,
        customerPhone,
        deliveredBy: deliveredBy || "",
        createdBy: userName,
        paymentMethod,
        totalAmount: totalAmount || "",
        date: formatDate(now),
        tokenNumber: newToken,
        delivered: false,
        items,
      };

      await push(ref(database, "invoices"), invoice);
      notify("Invoice created successfully!");
    }

    // RESET FORM
    setQuantities(Array(products.length).fill(0));
    setSchemes(Array(products.length).fill(0));
    setCustomerName("");
    setCustomerPhone("");
    setDeliveredBy("");
    setTotalAmount("");
    setPaymentMethod("Cash");
    setEditingInvoiceId(null);
  };

  // ------------------------
  // 🟦 PRINT INVOICE
  // ------------------------
  const printInvoice = (inv) => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head>
        <title>Jauhari Bill</title>
        <style>
          @media print { @page { size: 4in 6in; margin: .3in; } }
          body { font-family: Arial; font-size: 12px; }
          .line { margin: 3px 0; }
        </style>
      </head><body>
        <h2 style="text-align:center;">Jauhari Bill</h2>
        <div class="line"><b>Token:</b> ${inv.tokenNumber}</div>
        <div class="line"><b>Name:</b> ${inv.customerName}</div>
        <div class="line"><b>Phone:</b> ${inv.customerPhone}</div>
        <div class="line"><b>Date:</b> ${inv.date}</div>
        <div class="line"><b>Created By:</b> ${inv.createdBy}</div>
        <div class="line"><b>Delivered By:</b> ${inv.deliveredBy || "N/A"}</div>
        <div class="line"><b>Payment:</b> ${inv.paymentMethod}</div>
        ${inv.totalAmount ? `<div class="line"><b>Total:</b> ₹${inv.totalAmount}</div>` : ""}
        <br/>
        ${inv.items
          .map(
            (item) => `<div class="line">• ${item.quantity} x ${item.product}${
              item.scheme > 0 ? " (Scheme: " + item.scheme + ")" : ""
            }</div>`
          )
          .join("")}
        <script>window.print()</script>
      </body></html>
    `);
    w.document.close();
  };

  // ------------------------
  // 🟦 WHATSAPP SHARE
  // ------------------------
  const sendWhatsApp = (inv) => {
    const msg =
      `Jauhari Bill\nToken: ${inv.tokenNumber}\nCustomer: ${inv.customerName}\nCreated By: ${inv.createdBy}\nDelivered By: ${inv.deliveredBy || "N/A"}\nDate: ${inv.date}\nPayment: ${inv.paymentMethod}` +
      (inv.totalAmount ? `\nTotal: ₹${inv.totalAmount}` : "") +
      "\n" +
      inv.items
        .map(
          (i) =>
            `${i.quantity} x ${i.product}${
              i.scheme > 0 ? " (Scheme: " + i.scheme + ")" : ""
            }`
        )
        .join("\n");

    window.open(
      `https://wa.me/91${inv.customerPhone}?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };

  // ------------------------
  // 🟦 DELETE INVOICE
  // ------------------------
  const deleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await remove(ref(database, `invoices/${id}`));
    notify("Invoice deleted");
  };

  // ------------------------
  // 🟦 CSV EXPORT
  // ------------------------
  const downloadCSV = () => {
    const headers = [
      "Token",
      "Customer",
      "Phone",
      "Date",
      "Created By",
      "Payment",
      "Total",
      "Delivered",
      ...products.flatMap((p) => [p, p + " (Scheme)"]),
    ];

    const rows = invoices.map((inv) => {
      const map = Object.fromEntries(
        inv.items.map((i) => [i.product, [i.quantity, i.scheme]])
      );

      return [
        inv.tokenNumber,
        inv.customerName,
        inv.customerPhone,
        inv.date,
        inv.createdBy,
        inv.paymentMethod,
        inv.totalAmount,
        inv.delivered ? "Yes" : "No",
        ...products.flatMap((p) => map[p] || [0, 0]),
      ];
    });

    const csv =
      [headers, ...rows]
        .map((row) => row.map((v) => `"${v}"`).join(","))
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tokens.csv";
    a.click();
  };

  // ------------------------
  // 🟦 EDIT INVOICE
  // ------------------------
  const editInvoice = (inv) => {
    setEditingInvoiceId(inv.id);
    setCustomerName(inv.customerName);
    setCustomerPhone(inv.customerPhone);
    setDeliveredBy(inv.deliveredBy || "");
    setPaymentMethod(inv.paymentMethod);
    setTotalAmount(inv.totalAmount || "");

    const q = Array(products.length).fill(0);
    const s = Array(products.length).fill(0);

    inv.items.forEach(({ product, quantity, scheme }) => {
      const idx = products.indexOf(product);
      if (idx >= 0) {
        q[idx] = quantity;
        s[idx] = scheme || 0;
      }
    });

    setQuantities(q);
    setSchemes(s);
    window.scrollTo(0, 0);
  };

  // ------------------------
  // 🟦 DELIVERED TOGGLE
  // ------------------------
  const markAsDelivered = async (id) => {
    await update(ref(database, `invoices/${id}`), { delivered: true });
    notify("Marked as delivered");
  };

// BillingSystem.jsx – PART 3/3 (FINAL & CORRECTED CONTINUED)

  // ------------------------
  // 🟦 SMALL HELPERS (local state handlers)
  // ------------------------
  const handleQuantityChange = (index, value) => {
    const arr = [...quantities];
    arr[index] = Math.max(0, parseInt(value) || 0);
    setQuantities(arr);
  };

  const handleSchemeChange = (index, value) => {
    const arr = [...schemes];
    arr[index] = Math.max(0, parseFloat(value) || 0);
    setSchemes(arr);
  };

  const updateUserProfile = async () => {
    if (!editName.trim()) return notify("Enter a valid name");
    await update(ref(database, `users/${phone}`), { name: editName });
    setUserName(editName);
    notify("Profile updated");
    setShowProfile(false);
  };

  // ------------------------
  // 🟦 FILTER + PERFORMANCE: show latest 100 tokens for speed
  // ------------------------
  const MAX_TOKENS = 100;
  const filteredInvoices = invoices.filter((inv) => {
    const term = (searchTerm || "").toString().toLowerCase();
    if (!term) return true;
    if (role === "staff") {
      return inv.tokenNumber?.toString().includes(term);
    }
    return (
      (inv.customerName || "").toLowerCase().includes(term) ||
      inv.tokenNumber?.toString().includes(term)
    );
  });

  const visibleInvoices = filteredInvoices.slice(0, MAX_TOKENS);

  // ------------------------
  // 🟦 FINAL JSX (render)
  // ------------------------
  return (
    <div className="container">
      <h2>Jauhari Billing System</h2>
      {role === "boss" && <BossNavigation />}
      <Navigation role={role} />

      {notification && <div className="notification-banner">{notification}</div>}

      <p>
        Logged in as <strong>{userName}</strong> ({role}){" "}
        <button onClick={() => setShowProfile(true)}>Edit Profile</button>
      </p>

      {(role === "owner" || role === "boss") && (
        <div className="section">
          <button onClick={resetTokenNumber} style={{ marginLeft: 5 }}>
            Reset Token
          </button>
          <button
            onClick={() => navigate("/backup")}
            style={{ marginLeft: 8 }}
          >
            Backup & Restore
          </button>
        </div>
      )}

      {(role === "owner" || role === "manager" || role === "boss") && (
        <div className="section">
          <h4>Stock Live View</h4>

          <div style={{ marginBottom: 8 }}>
            <button onClick={handleCheckLatestStock}>
              Check & Correct Latest Stock
            </button>
          </div>

          <table className="stock-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Live Stock (Cartons)</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={p}>
                  <td>{p}</td>
                  <td>{(displayStock[i] || 0) + " cartons"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(role === "owner" || role === "manager" || role === "boss") && (
        <div className="section">
          <h4>Create Invoice</h4>
          <div className="product-row-space">
            <span className="product-label"><strong>Product</strong></span>
            <span className="product-input-heading"><strong>Qty</strong></span>
            <span className="product-input-heading"><strong>Scheme</strong></span>
          </div>

          {products.map((p, i) => (
            <div key={p} className="product-row-space">
              <span className="product-label">{p}</span>
              <input
                type="number"
                className="product-input-box"
                value={quantities[i]}
                onChange={(e) => handleQuantityChange(i, e.target.value)}
                min="0"
              />
              <input
                type="number"
                className="product-input-box"
                value={schemes[i]}
                onChange={(e) => handleSchemeChange(i, e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
          ))}

          <input
            placeholder="Customer Name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
          <input
            placeholder="Phone Number"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
          />
          <input
            placeholder="Delivered By (optional)"
            value={deliveredBy}
            onChange={(e) => setDeliveredBy(e.target.value)}
          />

          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <option value="Cash">Cash</option>
            <option value="Google Pay">Google Pay</option>
            <option value="Free">Free</option>
            <option value="Pay Later">Pay Later</option>
          </select>

          <input
            placeholder="Total Amount (optional)"
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
          />

          <button onClick={handleGenerateInvoice}>
            {editingInvoiceId ? "Update Invoice" : "Create Invoice"}
          </button>
        </div>
      )}

      <div className="section">
        <h4>Token History</h4>
        <input
          placeholder="Search by name or token"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {visibleInvoices.length === 0 ? (
          <p>No tokens found.</p>
        ) : (
          <>
            {filteredInvoices.length > MAX_TOKENS && (
              <p style={{ fontSize: 12, marginBottom: 8 }}>
                Showing latest {MAX_TOKENS} of {filteredInvoices.length} tokens. Refine search to see others.
              </p>
            )}

            {visibleInvoices.map((inv) => (
              <div key={inv.id} className={`token-card ${inv.delivered ? "delivered" : ""}`}>
                <strong>Token #{inv.tokenNumber}</strong> - {inv.customerName} ({inv.customerPhone})
                <br />
                <small>{inv.date}</small>
                <br />
                <small><strong>Bill Created By:</strong> {inv.createdBy}</small>
                <br />
                <small><strong>Delivered By:</strong> {inv.deliveredBy || "N/A"}</small>
                <br />
                <small><strong>Payment:</strong> {inv.paymentMethod}</small>
                {inv.totalAmount && (
                  <>
                    <br />
                    <small><strong>Total:</strong> ₹{inv.totalAmount}</small>
                  </>
                )}

                <ul>
                  {inv.items.map((item, idx) => (
                    <li key={idx}>
                      {item.quantity} x {item.product}
                      {item.scheme > 0 ? ` (Scheme: ${item.scheme})` : ""}
                    </li>
                  ))}
                </ul>

                <label>
                  <input
                    type="checkbox"
                    checked={inv.delivered || false}
                    disabled={inv.delivered}
                    onChange={() => markAsDelivered(inv.id)}
                  />
                  Delivered
                </label>

                {(role === "owner" || role === "manager" || role === "boss") && (
                  <>
                    <button onClick={() => printInvoice(inv)}>Print</button>
                    <button onClick={() => sendWhatsApp(inv)}>WhatsApp</button>
                    <button onClick={() => editInvoice(inv)}>Edit</button>
                    <button onClick={() => deleteInvoice(inv.id)}>Delete</button>
                  </>
                )}
              </div>
            ))}
          </>
        )}

        <button onClick={downloadCSV}>Download CSV</button>
      </div>

      {showProfile && (
        <div className="container">
          <h3>My Profile</h3>
          <p><strong>Phone:</strong> {phone}</p>
          <p><strong>Role:</strong> {role}</p>

          <label>Edit Name:</label>
          <input value={editName} onChange={(e) => setEditName(e.target.value)} />
          <button onClick={updateUserProfile}>Update</button>
          <button onClick={() => setShowProfile(false)} style={{ marginLeft: 10 }}>
            Back
          </button>
        </div>
      )}
    </div>
  );
}
